import React, {
  useEffect,
  useMemo,
  useState
} from "react";

import styled from "styled-components";

import {
  FaSearch,
  FaCheckCircle,
  FaMoneyBillWave,
  FaCashRegister,
  FaExclamationTriangle,
  FaTimes
} from "react-icons/fa";

import { toast } from "react-toastify";

import api from "../services/api";

import PagamentoModal from "../components/PagamentoModal";
import CaixaModal from "../components/CaixaModal";

/* =========================================================
   HELPERS
========================================================= */

const formatMoney = (value) => {

  return Number(value || 0).toLocaleString(
    "pt-MZ",
    {
      style:"currency",
      currency:"MZN"
    }
  );
};

const formatDateMYSQL = (date) => {

  if(!date) return "-";

  const d = new Date(date);

  const year = d.getFullYear();

  const month = String(
    d.getMonth() + 1
  ).padStart(2,"0");

  const day = String(
    d.getDate()
  ).padStart(2,"0");

  return `${year}-${month}-${day}`;
};

/* =========================================================
   ESTILOS
========================================================= */

const Container = styled.div`
  padding:40px;
  background:linear-gradient(
    145deg,
    #eef2ff,
    #f8fafc
  );

  min-height:100vh;
`;

const Card = styled.div`
  background:white;
  border-radius:25px;
  padding:35px;

  box-shadow:
    0 25px 60px rgba(0,0,0,0.08);
`;

const Header = styled.div`
  display:flex;
  justify-content:space-between;
  align-items:center;
  flex-wrap:wrap;
  gap:20px;
  margin-bottom:30px;
`;

const Title = styled.h2`
  display:flex;
  align-items:center;
  gap:10px;

  font-size:30px;
  color:#1e293b;
`;

const Filters = styled.div`
  display:flex;
  flex-wrap:wrap;
  gap:12px;
`;

const SearchBox = styled.div`
  display:flex;
  align-items:center;
  gap:10px;

  background:#f1f5f9;

  border:2px solid #e2e8f0;

  border-radius:50px;

  padding:12px 18px;

  min-width:320px;

  &:focus-within{
    border-color:#3b82f6;
    background:white;
  }

  input{
    border:none;
    background:transparent;
    outline:none;
    width:100%;
  }
`;

const DateInput = styled.input`
  padding:12px 15px;

  border-radius:50px;

  border:2px solid #e2e8f0;

  outline:none;

  &:focus{
    border-color:#3b82f6;
  }
`;

const Select = styled.select`
  padding:12px 15px;

  border-radius:50px;

  border:2px solid #e2e8f0;

  outline:none;

  background:white;

  &:focus{
    border-color:#3b82f6;
  }
`;

const ActionButton = styled.button`
  border:none;

  border-radius:50px;

  padding:10px 16px;

  background:${p=>p.bg || "#3b82f6"};

  color:white;

  display:flex;

  align-items:center;

  gap:8px;

  cursor:pointer;

  font-weight:600;

  &:hover{
    opacity:0.9;
  }
`;

const TotalsWrapper = styled.div`
  display:grid;

  grid-template-columns:
    repeat(auto-fit,minmax(220px,1fr));

  gap:20px;

  margin-bottom:30px;
`;

const TotalCard = styled.div`
  background:${p=>p.bg};

  color:white;

  padding:25px;

  border-radius:20px;

  h4{
    margin:0;
    opacity:0.9;
  }

  h3{
    margin:10px 0;
    font-size:30px;
  }

  span{
    font-size:14px;
  }
`;

const NoData = styled.div`
  text-align:center;

  padding:60px;

  color:#64748b;
`;

const Status = styled.span`
  padding:6px 12px;

  border-radius:50px;

  font-size:12px;

  font-weight:bold;

  background:
    ${p=>
      p.status==="PAGO"
        ? "#dcfce7"
        : p.status==="ATRASADO"
        ? "#fef3c7"
        : "#fee2e2"};

  color:
    ${p=>
      p.status==="PAGO"
        ? "#166534"
        : p.status==="ATRASADO"
        ? "#92400e"
        : "#991b1b"};
`;

const Multa = styled.span`
  color:#dc2626;
  font-weight:bold;
`;

const FooterInfo = styled.div`
  margin-top:25px;

  display:flex;

  justify-content:space-between;

  flex-wrap:wrap;

  gap:10px;

  color:#64748b;
`;

/* =========================================================
   ACCORDION
========================================================= */

const AccordionCard = styled.div`
  border:1px solid #e2e8f0;

  border-radius:20px;

  overflow:hidden;

  margin-bottom:20px;

  background:white;
`;

const AccordionHeader = styled.div`
  padding:20px;

  background:#f8fafc;

  display:flex;

  justify-content:space-between;

  align-items:center;

  cursor:pointer;

  &:hover{
    background:#eef2ff;
  }
`;

const AccordionTitle = styled.div`

  h3{
    margin:0;
    color:#1e293b;
  }

  span{
    color:#64748b;
    font-size:13px;
  }
`;

const ExpandButton = styled.div`
  background:#dbeafe;

  color:#2563eb;

  padding:8px 14px;

  border-radius:50px;

  font-size:13px;

  font-weight:600;
`;

const AccordionBody = styled.div`
  background:white;
`;

const MensalidadeRow = styled.div`
  display:grid;

  grid-template-columns:
    repeat(6,1fr);

  gap:20px;

  padding:18px 20px;

  border-top:1px solid #e2e8f0;

  align-items:center;

  &:hover{
    background:#f8fafc;
  }
`;

const Label = styled.div`
  font-size:12px;
  color:#64748b;
  margin-bottom:4px;
`;

const Value = styled.div`
  font-size:14px;
  font-weight:600;
`;

/* =========================================================
   COMPONENTE
========================================================= */

export default function MensalidadesPage(){

  const [mensalidades,setMensalidades] =
    useState([]);

  const [busca,setBusca] =
    useState("");

  const [statusFiltro,setStatusFiltro] =
    useState("");

  const [dataDe,setDataDe] =
    useState("");

  const [dataAte,setDataAte] =
    useState("");

  const [modalAberto,setModalAberto] =
    useState(false);

  const [modalTipo,setModalTipo] =
    useState("");

  const [
    mensalidadeSelecionada,
    setMensalidadeSelecionada
  ] = useState(null);

  const [caixaAberto,setCaixaAberto] =
    useState(false);

  const [expandido,setExpandido] =
    useState(null);

  /* =========================================================
     LOAD
  ========================================================= */

  const loadMensalidades = async ()=>{

    try{

      const res =
        await api.get("/mensalidades");

      setMensalidades(res.data);

    }catch{

      toast.error(
        "Erro ao carregar mensalidades"
      );

    }
  };

  const checkCaixa = async ()=>{

    try{

      const res =
        await api.get("/caixa/status");

      setCaixaAberto(
        res.data.aberto
      );

    }catch{

      setCaixaAberto(false);

    }
  };

  useEffect(()=>{

    loadMensalidades();

    checkCaixa();

  },[]);

  /* =========================================================
     FILTRO
  ========================================================= */

  const filtradas = useMemo(()=>{

    return mensalidades.filter((m)=>{

      const termo =
        busca.toLowerCase().trim();

      const aluno =
        m.aluno_nome?.toLowerCase() || "";

      const numero =
        m.numero_estudante?.toLowerCase() || "";

      const status =
        m.status_mensalidade?.toLowerCase() || "";

      const data =
        formatDateMYSQL(
          m.mes_referencia
        );

      const matchTexto =

        aluno.includes(termo) ||

        numero.includes(termo) ||

        status.includes(termo) ||

        data.includes(termo);

      const matchStatus =

        statusFiltro
          ? m.status_mensalidade ===
            statusFiltro
          : true;

      let matchData = true;

      const dataMensalidade =
        new Date(m.mes_referencia);

      if(dataDe){

        matchData =
          dataMensalidade >=
          new Date(dataDe);

      }

      if(dataAte){

        matchData =
          matchData &&
          dataMensalidade <=
          new Date(dataAte);

      }

      return (
        matchTexto &&
        matchStatus &&
        matchData
      );

    });

  },[
    mensalidades,
    busca,
    statusFiltro,
    dataDe,
    dataAte
  ]);
  const calcularResumoDetalhado = (mensalidades) => {

    const total = mensalidades.length;

    const pagos = mensalidades.filter(
      m => m.status_mensalidade === "PAGO"
    ).length;

    const pendentes = mensalidades.filter(
      m => m.status_mensalidade === "PENDENTE"
    ).length;

    const atrasados = mensalidades.filter(
      m => m.status_mensalidade === "ATRASADO"
    ).length;

    const divida = mensalidades
      .filter(m => m.status_mensalidade !== "PAGO")
      .reduce((s, m) => s + Number(m.valor_final || 0), 0);

    return {
      total,
      pagos,
      pendentes,
      atrasados,
      divida
    };
  };
 const calcularResumoAluno = (mensalidades) => {

  const total = mensalidades.reduce(
    (s, m) => s + Number(m.valor_final || 0),
    0
  );

  const pago = mensalidades
    .filter(m => m.status_mensalidade === "PAGO")
    .reduce((s, m) => s + Number(m.valor_final || 0), 0);

  const divida = total - pago;

  return { total, pago, divida };
};

  /* =========================================================
     AGRUPAR POR ALUNO
  ========================================================= */
const agrupadas = useMemo(() => {

  const grupos = {};

  filtradas.forEach((m) => {

    const alunoId = m.aluno_id || m.numero_estudante;

    if (!grupos[alunoId]) {

      grupos[alunoId] = {
        aluno: m.aluno_nome,
        numero: m.numero_estudante,
        mensalidades: []
      };

    }

    grupos[alunoId].mensalidades.push(m);

  });

  let lista = Object.values(grupos).map(g => {

    const resumo = calcularResumoDetalhado(g.mensalidades);

    return {
      ...g,
      ...resumo
    };

  });

  // quem deve mais primeiro
  lista.sort((a, b) => b.divida - a.divida);

  return lista;

}, [filtradas]);

  /* =========================================================
     TOTAIS
  ========================================================= */

  const calcular = (status)=>
    filtradas.filter(
      m=>m.status_mensalidade===status
    );

  const totalGeralValor =
    filtradas.reduce(
      (s,m)=>
        s + Number(m.valor_final || 0),
      0
    );

  /* =========================================================
     MODAIS
  ========================================================= */

  const abrirModal = (
    tipo,
    mensalidade = null
  )=>{

    setModalTipo(tipo);

    setMensalidadeSelecionada(
      mensalidade
    );

    setModalAberto(true);
  };

  const handlePagarClick = (
    mensalidade
  )=>{

    if(!caixaAberto){

      toast.info(
        "Abra o caixa antes de pagar"
      );

      abrirModal("CAIXA");

    }else{

      abrirModal(
        "PAGAMENTO",
        mensalidade
      );

    }
  };

  /* =========================================================
     LIMPAR FILTROS
  ========================================================= */

  const limparFiltros = ()=>{

    setBusca("");

    setStatusFiltro("");

    setDataDe("");

    setDataAte("");
  };
const getPercentualMulta = (multa, valorBase) => {

  const multaNum = Number(multa || 0);
  const baseNum = Number(valorBase || 0);

  if(baseNum <= 0 || multaNum <= 0){
    return 0;
  }

  return Math.round(
    (multaNum / baseNum) * 100
  );
};

const PercentBadge = styled.span`

  display:inline-flex;
  align-items:center;
  justify-content:center;

  padding:4px 10px;

  border-radius:50px;

  font-size:11px;
  font-weight:700;

  color:white;

  background:${p =>

    p.percent >= 100
      ? "linear-gradient(135deg,#991b1b,#dc2626)"
      : p.percent >= 50
      ? "linear-gradient(135deg,#b45309,#f59e0b)"
      : "linear-gradient(135deg,#2563eb,#3b82f6)"
  };

  box-shadow:
    0 4px 12px rgba(0,0,0,0.15);

`;

  /* =========================================================
     RENDER
  ========================================================= */

  return(
    <Container>

      <Card>

        {/* HEADER */}

        <Header>

          <Title>

            <FaMoneyBillWave/>

            Gestão de Mensalidades

          </Title>

          

          <Filters>

            <SearchBox>

              <FaSearch color="#64748b"/>

              <input
                placeholder="
                Pesquisar aluno,
                número,
                data,
                status...
                "
                value={busca}
                onChange={(e)=>
                  setBusca(
                    e.target.value
                  )
                }
              />

            </SearchBox>

            <Select
              value={statusFiltro}
              onChange={(e)=>
                setStatusFiltro(
                  e.target.value
                )
              }
            >

              <option value="">
                Todos Status
              </option>

              <option value="PENDENTE">
                PENDENTE
              </option>

              <option value="ATRASADO">
                ATRASADO
              </option>

              <option value="PAGO">
                PAGO
              </option>

            </Select>

            <DateInput
              type="date"
              value={dataDe}
              onChange={(e)=>
                setDataDe(
                  e.target.value
                )
              }
            />

            <DateInput
              type="date"
              value={dataAte}
              onChange={(e)=>
                setDataAte(
                  e.target.value
                )
              }
            />

            <ActionButton
              bg="#64748b"
              onClick={limparFiltros}
            >

              <FaTimes/>

              Limpar

            </ActionButton>

            {caixaAberto && (

              <ActionButton
                bg="#f59e0b"
                onClick={()=>
                  abrirModal("CAIXA")
                }
              >

                <FaCashRegister/>

                Fechar Caixa

              </ActionButton>

            )}

          </Filters>

          

        </Header>

        {/* TOTAIS */}

        <TotalsWrapper>

          <TotalCard bg="#dc2626">

            <h4>PENDENTE</h4>

            <h3>
              {calcular("PENDENTE").length}
            </h3>

            <span>

              {
                formatMoney(

                  calcular("PENDENTE")
                  .reduce(
                    (s,m)=>

                      s + Number(
                        m.valor_final || 0
                      ),

                    0
                  )

                )
              }

            </span>

          </TotalCard>

          <TotalCard bg="#f59e0b">

            <h4>ATRASADO</h4>

            <h3>
              {calcular("ATRASADO").length}
            </h3>

            <span>

              {
                formatMoney(

                  calcular("ATRASADO")
                  .reduce(
                    (s,m)=>

                      s + Number(
                        m.valor_final || 0
                      ),

                    0
                  )

                )
              }

            </span>

          </TotalCard>

          <TotalCard bg="#16a34a">

            <h4>PAGO</h4>

            <h3>
              {calcular("PAGO").length}
            </h3>

            <span>

              {
                formatMoney(

                  calcular("PAGO")
                  .reduce(
                    (s,m)=>

                      s + Number(
                        m.valor_final || 0
                      ),

                    0
                  )

                )
              }

            </span>

          </TotalCard>

          <TotalCard bg="#1e293b">

            <h4>TOTAL GERAL</h4>

            <h3>
              {filtradas.length}
            </h3>

            <span>
              {formatMoney(totalGeralValor)}
            </span>

          </TotalCard>

        </TotalsWrapper>

        {/* LISTA */}

        {

          agrupadas.length === 0 ? (

            <NoData>

              <FaExclamationTriangle
                size={50}
              />

              <h3>
                Nenhum registro encontrado
              </h3>

            </NoData>

          ) : (

            agrupadas.map((grupo,index)=>(

              <AccordionCard key={index}>

                {/* HEADER */}

                <AccordionCard
                  key={index}
                  style={{
                    borderLeft: grupo.divida > 0
                      ? "5px solid #dc2626"
                      : "5px solid #16a34a"
                  }}
                >
                  
                </AccordionCard>

               <AccordionHeader
                onClick={() =>
                  setExpandido(expandido === index ? null : index)
                }
              >

              <AccordionTitle>

                <h3>{grupo.aluno}</h3>

                <span>
                  {grupo.numero} • {grupo.total} mensalidades
                </span>

                <div style={{ marginTop: 8, fontSize: 13 }}>

                  <span style={{ color: "#16a34a" }}>
                    Pago: {grupo.pagos}
                  </span>

                  {" | "}

                  <span style={{ color: "#f59e0b" }}>
                    Pendentes: {grupo.pendentes}
                  </span>

                  {" | "}

                  <span style={{ color: "#ef4444" }}>
                    Atrasados: {grupo.atrasados}
                  </span>

                </div>

                <div style={{ marginTop: 6 }}>

                  <strong>Dívida: </strong>

                  <span style={{
                    color: grupo.divida > 0 ? "#dc2626" : "#16a34a"
                  }}>
                    {formatMoney(grupo.divida)}
                  </span>

                </div>

              </AccordionTitle>
                <ExpandButton>
                  {expandido === index ? "Ocultar" : "Ver"}
                </ExpandButton>

              </AccordionHeader>

                {/* BODY */}

                {
                  expandido === index && (

                    <AccordionBody>

                      {

                        grupo.mensalidades
                        .map((m)=>(

                          <MensalidadeRow
                            key={
                              m.mensalidade_id
                            }
                          >

                            {/* DATA */}

                            <div>

                              <Label>
                                Ano-Mês-Dia
                              </Label>

                              <Value>

                                {
                                  formatDateMYSQL(
                                    m.mes_referencia
                                  )
                                }

                              </Value>

                            </div>

                            {/* VALOR */}

                            <div>

                              <Label>
                                Valor Base
                              </Label>

                              <Value>

                                {
                                  formatMoney(
                                    m.valor_base
                                  )
                                }

                              </Value>

                            </div>

                            {/* MULTA */}

                            <div>

                              <Label>
                                Multa
                              </Label>

                              <Value>
                               <PercentBadge
  percent={
    getPercentualMulta(
      m.multa,
      m.valor_base
    )
  }
>

  {
    formatMoney(m.multa)
  }

  {" • "}

  {
    getPercentualMulta(
      m.multa,
      m.valor_base
    )
  }%

</PercentBadge>

                              </Value>

                            </div>

                            {/* TOTAL */}

                            <div>

                              <Label>
                                Valor Final
                              </Label>

                              <Value>

                                {
                                  formatMoney(
                                    m.valor_final
                                  )
                                }

                              </Value>

                            </div>

                            {/* STATUS */}

                            <div>

                              <Label>
                                Estado
                              </Label>

                              <Status
                                status={
                                  m.status_mensalidade
                                }
                              >

                                {
                                  m.status_mensalidade
                                }

                              </Status>

                            </div>

                            {/* BOTÃO */}

                            <div>

                              <Label>
                                Pagamento
                              </Label>

                              {
                                m.status_mensalidade !==
                                "PAGO" ? (

                                  <ActionButton
                                    bg="#f59e0b"
                                    onClick={()=>{

                                      handlePagarClick(
                                        m
                                      );

                                    }}
                                  >

                                    <FaMoneyBillWave/>

                                    Pagar

                                  </ActionButton>

                                ) : (

                                  <FaCheckCircle
                                    color="green"
                                    size={22}
                                  />

                                )
                              }

                            </div>

                          </MensalidadeRow>

                        ))

                      }

                    </AccordionBody>

                  )
                }

              </AccordionCard>

            ))

          )

        }

        {/* FOOTER */}

        <FooterInfo>

          <div>

            <strong>
              Registros:
            </strong>

            {" "}

            {filtradas.length}

          </div>

          <div>

            <strong>
              Total Financeiro:
            </strong>

            {" "}

            {formatMoney(totalGeralValor)}

          </div>

        </FooterInfo>

      </Card>

      {/* MODAIS */}

      {

        modalAberto &&
        modalTipo === "PAGAMENTO" && (

          <PagamentoModal

            mensalidade={
              mensalidadeSelecionada
            }

            onClose={()=>
              setModalAberto(false)
            }

            onSuccess={()=>{

              setModalAberto(false);

              loadMensalidades();

            }}

          />

        )

      }

      {

        modalAberto &&
        modalTipo === "CAIXA" && (

          <CaixaModal

            onClose={()=>
              setModalAberto(false)
            }

            onSuccess={()=>{

              setModalAberto(false);

              checkCaixa();

              loadMensalidades();

            }}

          />

        )

      }

    </Container>
  );
}